import { useEffect, useMemo, useRef, useState } from "react";
import "./ShopPage.css";
import { api } from "../utils/api";
import { ProductGrid } from "../components/ProductGrid";
import { Cart } from "../components/Cart";
import { useToast } from "@forge/ui";
import type { Product, CartItem as CartItemType } from "../types";
import type {
  CheckoutSessionResponse,
  ProductsResponse,
} from "../types/api";

export function ShopPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [filter, setFilter] = useState<"All" | Product["category"]>("All");
  const [selected, setSelected] = useState<Product | null>(null);
  const [cart, setCart] = useState<CartItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const cartButtonRef = useRef<HTMLButtonElement>(null);
  const productModalRef = useRef<HTMLDivElement>(null);
  const productTriggerRef = useRef<HTMLElement | null>(null);
  const { show } = useToast();

  // Fetch products from API on mount
  useEffect(() => {
    let mounted = true;
    const fetchProducts = async () => {
      try {
        const data = await api.get<ProductsResponse>("/api/user/products");
        const list = (data?.products ?? []) as unknown as Product[];
        if (mounted) setProducts(list);
      } catch (_e) {
        if (mounted) setError("Failed to load products");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchProducts();
    return () => {
      mounted = false;
    };
  }, []);

  // Product detail modal: focus management + Escape to close
  useEffect(() => {
    if (!selected) return;

    const trigger = document.activeElement as HTMLElement | null;
    productTriggerRef.current = trigger;

    const focusFirst = () => {
      const modal = productModalRef.current;
      if (!modal) return;
      const first = modal.querySelector<HTMLElement>(
        "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
      );
      first?.focus();
    };
    focusFirst();
    const focusTimer = window.setTimeout(focusFirst, 0);

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSelected(null);
      }
    };
    document.addEventListener("keydown", handleKey);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKey);
      const t = productTriggerRef.current;
      productTriggerRef.current = null;
      if (t && document.body.contains(t)) {
        t.focus();
      }
    };
  }, [selected]);

  // Cart drawer: focus management + Escape to close
  useEffect(() => {
    if (!cartOpen) return;

    const focusFirst = () => {
      const panel = document.getElementById("forge-cart-panel");
      if (!panel) return;
      const first = panel.querySelector<HTMLElement>(
        "button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
      );
      first?.focus();
    };
    focusFirst();
    const focusTimer = window.setTimeout(focusFirst, 0);

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setCartOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);

    const cartButtonAtStart = cartButtonRef.current;
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKey);
      if (cartButtonAtStart && document.body.contains(cartButtonAtStart)) {
        cartButtonAtStart.focus();
      }
    };
  }, [cartOpen]);

  const categories = useMemo<(typeof filter)[]>(
    () => ["All", "Merch", "Supplements"],
    [],
  );
  const filteredProducts = useMemo(() => {
    if (filter === "All") return products;
    // ensure type safety: filter by product category
    return products.filter((p) => p.category === filter);
  }, [products, filter]);

  // Cart helpers
  const addToCart = (product: Product) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product.id === product.id ? { ...i, quantity: i.quantity + 1 } : i,
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const setQuantity = (productId: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((i) =>
          i.product.id === productId
            ? { ...i, quantity: Math.max(0, i.quantity + delta) }
            : i,
        )
        .filter((i) => i.quantity > 0),
    );
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((i) => i.product.id !== productId));
  };

  // Checkout flow
  const handleCheckout = async () => {
    if (!cart.length) return;
    setCheckoutLoading(true);
    try {
      const payload = {
        items: cart.map((ci) => ({
          product_id: ci.product.id,
          quantity: ci.quantity,
        })),
      };
      const res = await api.post<CheckoutSessionResponse>(
        "/api/stripe/create-payment-session",
        payload,
      );
      const url = res?.url;
      if (url) {
        window.location.assign(url);
      } else {
        show("Unable to start checkout", "error");
      }
    } catch {
      show("Checkout failed", "error");
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Render
  const hasProducts = filteredProducts.length > 0;

  return (
    <div
      className="shop-page"
      style={{
        minHeight: "100vh",
        padding: 16,
        backgroundColor: "#1a1a1a",
        color: "#fff",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 28 }}>Shop</h1>
        <button
          ref={cartButtonRef}
          aria-label="Open cart"
          aria-expanded={cartOpen}
          aria-controls="forge-cart-panel"
          onClick={() => setCartOpen((s) => !s)}
          style={{
            backgroundColor: "#ff6b35",
            color: "#000",
            border: "none",
            padding: "10px 14px",
            borderRadius: 8,
            fontWeight: 700,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          Cart
          <span
            style={{
              background: "#000",
              color: "#fff",
              borderRadius: 999,
              padding: "2px 8px",
              fontWeight: 700,
            }}
          >
            {cart.reduce((n, i) => n + i.quantity, 0)}
          </span>
        </button>
      </header>

      <div
        className="category-bar"
        style={{
          display: "flex",
          gap: 8,
          overflowX: "auto",
          paddingTop: 4,
          paddingBottom: 8,
        }}
      >
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            style={{
              padding: "8px 12px",
              borderRadius: 20,
              border: "1px solid #2a2a2a",
              background: filter === cat ? "#2a2a2a" : "#1a1a1a",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="placeholder-content" style={{ textAlign: "center" }}>
          <p>Loading products...</p>
        </div>
      ) : error ? (
        <div className="placeholder-content" style={{ textAlign: "center" }}>
          <p>{error}</p>
        </div>
      ) : (
        <div>
          {hasProducts ? (
            <ProductGrid
              products={filteredProducts}
              onAddToCart={addToCart}
              onViewDetails={(p) => setSelected(p)}
            />
          ) : (
            <div
              className="placeholder-content"
              style={{ textAlign: "center" }}
            >
              <p>No products available yet!</p>
            </div>
          )}
        </div>
      )}

      {/* Product detail modal */}
      {selected && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="shop-product-detail-title"
          ref={productModalRef}
          onClick={() => setSelected(null)}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "#1a1a1a",
              borderRadius: 12,
              padding: 20,
              width: "min(540px, 90vw)",
              color: "#fff",
              border: "1px solid #2a2a2a",
            }}
          >
            <div style={{ display: "flex", gap: 16 }}>
              <img
                src={selected.image}
                alt={selected.name}
                style={{
                  width: 180,
                  height: 180,
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
              <div style={{ flex: 1 }}>
                <h3 id="shop-product-detail-title" style={{ marginTop: 0 }}>
                  {selected.name}
                </h3>
                <p style={{ marginTop: 6, color: "#ddd" }}>
                  {selected.description ?? ""}
                </p>
                <p style={{ marginTop: 6 }}>
                  Price: <strong>${selected.price.toFixed(2)}</strong>
                </p>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 12,
                gap: 8,
              }}
            >
              <button
                onClick={() => {
                  addToCart(selected);
                  setSelected(null);
                }}
                style={{
                  backgroundColor: "#ff6b35",
                  border: "none",
                  padding: "10px 14px",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Add to Cart
              </button>
              <button
                onClick={() => setSelected(null)}
                style={{
                  backgroundColor: "#333",
                  color: "#fff",
                  border: "none",
                  padding: "10px 14px",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cart drawer */}
      <div
        id="forge-cart-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="forge-cart-title"
        style={{
          position: "fixed",
          right: 0,
          top: 0,
          width: 360,
          height: "100vh",
          backgroundColor: "#1a1a1a",
          borderLeft: "1px solid #2a2a2a",
          padding: 16,
          overflowY: "auto",
          display: cartOpen ? "block" : "none",
        }}
      >
        <h2
          id="forge-cart-title"
          style={{ position: "absolute", left: -10000, top: "auto" }}
        >
          Cart
        </h2>
        <Cart
          items={cart}
          onQuantityChange={setQuantity}
          onRemove={removeFromCart}
          onCheckout={handleCheckout}
          isLoading={checkoutLoading}
        />
      </div>
    </div>
  );
}
